import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { message } from 'antd';
import axios from 'axios';
import { API_ENDPOINTS } from '../utils/apiConfig';

// Types for authentication
interface User {
  id: number;
  username: string;
  role: 'Admin' | 'Leader' | 'Employee';
  name: string;
  team?: string | null;
  allowMultipleTasks?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Handle token expiration and redirect to login
   */
  const handleTokenExpiration = () => {
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Clear state
    setToken(null);
    setUser(null);

    // Clear authorization header
    delete axios.defaults.headers.common['Authorization'];

    // Show message to user
    message.error('Your session has expired. Please login again.');

    // Redirect to login page
    window.location.href = '/login';
  };

  // Set up axios interceptors for token expiration handling
  useEffect(() => {
    // Request interceptor to add token to all requests
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token expiration
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Token is expired or invalid
          handleTokenExpiration();
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Check for existing token on app load
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
        
        // Set default authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
        
        // Only verify token for Admin and Leader roles
        const parsedUser = JSON.parse(savedUser);
        if (parsedUser.role === 'Admin' || parsedUser.role === 'Leader') {
          // Verify token is still valid by making a test request
          // This will trigger the interceptor if token is expired
          axios.get(API_ENDPOINTS.DATA_A + '?page=1&limit=1').catch(() => {
            // If the request fails due to 401, the interceptor will handle it
            // We don't need to do anything here as the interceptor will redirect
          });
        }
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    
    setLoading(false);
  }, []);

  /**
   * Login function
   * @param username - User's username
   * @param password - User's password
   * @returns Promise<boolean> - True if login successful, false otherwise
   */
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      const response = await axios.post(API_ENDPOINTS.LOGIN, {
        username,
        password,
      });

      const { access_token, user: userData } = response.data;

      // Save to localStorage
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Update state
      setToken(access_token);
      setUser(userData);

      // Set default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      message.success(`Welcome back, ${userData.username}!`);
      return true;
    } catch (error: any) {
      console.error('Login error:', error);
      
      if (error.response?.status === 401) {
        message.error('Invalid username or password');
      } else {
        message.error('Login failed. Please try again.');
      }
      
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout function
   */
  const logout = () => {
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Clear state
    setToken(null);
    setUser(null);

    // Clear authorization header
    delete axios.defaults.headers.common['Authorization'];

    message.success('Logged out successfully');
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!token && !!user,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 